import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Clock, ArrowRight, Heart, Zap, Users, Coffee, X, Upload, Send, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>('');

  const jobs = [
    {
      title: 'Senior React Developer',
      type: 'Full-time',
      location: 'Hyderabad / Remote',
      department: 'Engineering'
    },
    {
      title: 'UI/UX Designer',
      type: 'Full-time',
      location: 'Hyderabad',
      department: 'Design'
    },
    {
      title: 'Digital Marketing Specialist',
      type: 'Full-time',
      location: 'Remote',
      department: 'Marketing'
    },
    {
      title: 'Node.js Backend Engineer',
      type: 'Full-time',
      location: 'Hyderabad / Remote',
      department: 'Engineering'
    }
  ];

  const perks = [
    { icon: Heart, title: 'Health & Wellness', desc: 'Comprehensive health insurance for you and your family.' },
    { icon: Zap, title: 'Rapid Growth', desc: 'Continuous learning opportunities and career advancement.' },
    { icon: Users, title: 'Inclusive Culture', desc: 'Work in a diverse, friendly, and supportive environment.' },
    { icon: Coffee, title: 'Flexible Work', desc: 'Hybrid and remote options to maintain work-life balance.' }
  ];

  const handleApply = (job: any) => {
    setSelectedJob(job);
    setIsSubmitted(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFileName(e.target.files[0].name);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const loadingToast = toast.loading('Submitting application...', {
      style: {
        borderRadius: '10px',
        background: '#4F46E5',
        color: '#fff',
      },
    });

    const formData = new FormData(e.currentTarget);
    formData.append("access_key", "9fcd84f7-aceb-44de-9438-ab9a40a73fb7");
    formData.append("subject", `New Job Application: ${selectedJob.title}`);
    formData.append("from_name", "OETechs Careers Portal");
    // Web3Forms sends to the email associated with the key (usually info@oetechs.com)
    // To send to multiple emails, it's best configured in the Web3Forms dashboard.
    
    if (fileInputRef.current?.files?.[0]) {
      formData.append("attachment", fileInputRef.current.files[0]);
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); 

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.success) {
        toast.dismiss(loadingToast);
        toast.success('Application submitted successfully!', {
          icon: '🚀',
          style: {
            borderRadius: '10px',
            background: '#4F46E5',
            color: '#fff',
          },
        });
        setIsSubmitted(true);
        setFileName('');
      } else {
        throw new Error(data.message || "Failed to submit application");
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      const errorMsg = error.name === 'AbortError' 
        ? "Upload timed out. Please check your file size and connection."
        : (error.message || "Error submitting application. Please try again.");
      
      toast.error(errorMsg, {
        style: {
          borderRadius: '10px',
          background: '#4F46E5',
          color: '#fff',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] pb-24 transition-colors duration-500">
      {/* Hero */}
      <section className="bg-white dark:bg-[#001a35] border-b border-slate-100 dark:border-white/5 py-24">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-blue-600 font-semibold uppercase tracking-widest text-sm mb-4 block">Careers</span>
            <h1 className="text-4xl md:text-6xl font-black text-[#4F46E5] dark:text-white mb-6">Build the Future of Digital</h1>
            <p className="text-slate-500 dark:text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
              Join OETechs and be part of a team that's redefining how businesses interact with the digital world. We're looking for passionate individuals to join our journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-24 max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
           {perks.map((perk, i) => (
             <div key={i} className="bg-white dark:bg-[#001a35] p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-white/10 hover:shadow-xl transition-all">
                <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mb-6">
                   {React.createElement(perk.icon, { size: 24 })}
                </div>
                <h4 className="font-semibold text-[#4F46E5] dark:text-white mb-2">{perk.title}</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{perk.desc}</p>
             </div>
           ))}
        </div>
      </section>

      {/* Jobs */}
      <section className="max-w-5xl mx-auto px-4">
         <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl font-semibold text-[#4F46E5] dark:text-white">Open Positions</h2>
            <div className="text-sm font-semibold text-blue-600 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
               {jobs.length} Openings
            </div>
         </div>

         <div className="space-y-6">
            {jobs.map((job, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                onClick={() => handleApply(job)}
                className="bg-white dark:bg-[#001a35] p-6 md:p-8 rounded-[2rem] shadow-sm border border-slate-100 dark:border-white/10 hover:shadow-xl hover:border-blue-100 dark:hover:border-blue-900/50 transition-all group cursor-pointer"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                   <div className="space-y-4">
                      <div className="flex items-center gap-3">
                         <span className="text-[10px] font-black uppercase tracking-widest text-blue-500 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full">
                           {job.department}
                         </span>
                         <span className="text-[10px] font-black uppercase tracking-widest text-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1 rounded-full">
                           {job.type}
                         </span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-semibold text-[#4F46E5] dark:text-white group-hover:text-blue-600 transition-colors">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-6 text-slate-400">
                         <div className="flex items-center gap-2 text-sm">
                            <MapPin size={16} /> {job.location}
                         </div>
                         <div className="flex items-center gap-2 text-sm">
                            <Clock size={16} /> Posted 2 days ago
                         </div>
                      </div>
                   </div>
                   
                   <div className="shrink-0">
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleApply(job); }}
                        className="w-full md:w-auto px-8 py-4 bg-[#4F46E5] dark:bg-blue-600 text-white rounded-2xl font-semibold flex items-center justify-center gap-3 group-hover:bg-blue-600 dark:group-hover:bg-blue-500 transition-all"
                      >
                         Apply Now <ArrowRight size={18} />
                      </button>
                   </div>
                </div>
              </motion.div>
            ))}
         </div>
      </section>

      {/* Talent Pool */}
      <section className="mt-24 max-w-7xl mx-auto px-4">
         <div className="bg-gradient-to-br from-blue-600 to-indigo-800 rounded-[3.5rem] p-12 md:p-20 text-center relative overflow-hidden text-white">
            <div className="relative z-10">
               <h2 className="text-3xl md:text-5xl font-semibold mb-6">Don't see the right role?</h2>
               <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
                 We're always looking for talented people to join our talent pool. Send us your resume and we'll let you know when a matching role opens up.
               </p>
               <button 
                 onClick={() => handleApply({ title: 'General Application', department: 'Talent Pool', type: 'Any' })}
                 className="px-10 py-5 bg-[#4F46E5] text-[#fff] rounded-2xl font-semibold hover:scale-105 transition-transform"
               >
                 Join Our Talent Pool
               </button>
            </div>
            {/* Graphics */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-48 -mt-48" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl -ml-32 -mb-32" />
         </div>
      </section>

      {/* Application Modal */}
      <AnimatePresence>
        {selectedJob && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedJob(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white dark:bg-[#001a35] rounded-[2.5rem] shadow-2xl overflow-hidden"
            >
              {isSubmitted ? (
                <div className="p-12 md:p-16 text-center">
                  <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8">
                    <CheckCircle2 size={40} />
                  </div>
                  <h2 className="text-3xl font-semibold text-[#4F46E5] dark:text-white mb-4">Application Received!</h2>
                  <p className="text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">
                    Thank you for applying for the <strong>{selectedJob.title}</strong> position. We've sent your details to <strong>venkat@oetechs.com</strong> and our team will review your profile shortly.
                  </p>
                  <button 
                    onClick={() => setSelectedJob(null)}
                    className="px-10 py-4 bg-blue-600 text-white rounded-2xl font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="p-6 md:p-8 bg-slate-50 dark:bg-white/5 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-semibold text-[#4F46E5] dark:text-white">Apply for Role</h2>
                      <p className="text-sm text-blue-600 font-semibold">{selectedJob.title}</p>
                    </div>
                    <button 
                      onClick={() => setSelectedJob(null)}
                      className="w-10 h-10 rounded-full bg-white dark:bg-white/10 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6 overflow-y-auto max-h-[70vh]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-semibold text-slate-400 uppercase">Full Name</label>
                        <input required name="name" type="text" placeholder="John Doe" className="w-full bg-slate-50 dark:bg-white/5 border-none rounded-2xl py-4 px-6 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-semibold text-slate-400 uppercase">Email Address</label>
                        <input required name="email" type="email" placeholder="john@example.com" className="w-full bg-slate-50 dark:bg-white/5 border-none rounded-2xl py-4 px-6 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-semibold text-slate-400 uppercase">Phone Number</label>
                        <input required name="phone" type="tel" placeholder="+91 0000 000 000" className="w-full bg-slate-50 dark:bg-white/5 border-none rounded-2xl py-4 px-6 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-semibold text-slate-400 uppercase">LinkedIn / Portfolio</label>
                        <input name="portfolio" type="url" placeholder="https://..." className="w-full bg-slate-50 dark:bg-white/5 border-none rounded-2xl py-4 px-6 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-semibold text-slate-400 uppercase">Attach Resume (PDF)</label>
                      <div 
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border-2 border-dashed border-slate-200 dark:border-white/10 rounded-[2rem] p-8 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-all cursor-pointer group"
                      >
                        <input 
                          type="file" 
                          ref={fileInputRef} 
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx" 
                          className="hidden" 
                        />
                        <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                          <Upload size={24} />
                        </div>
                        <p className="text-sm font-semibold text-[#4F46E5] dark:text-white">
                          {fileName || 'Click to upload your resume'}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-2">Max file size: 5MB (PDF, DOC, DOCX)</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-semibold text-slate-400 uppercase">Cover Message</label>
                      <textarea required name="cover_message" rows={3} placeholder="Tell us why you're a great fit..." className="w-full bg-slate-50 dark:bg-white/5 border-none rounded-2xl py-4 px-6 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none"></textarea>
                    </div>

                    <button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full py-5 bg-blue-600 text-white rounded-2xl font-semibold flex items-center justify-center gap-3 hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>Processing...</>
                      ) : (
                        <>Submit Application <Send size={18} /></>
                      )}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Careers;
